import React, { useContext } from 'react';
import classes from './PythonPanelOptions.module.scss';
import { ActionMenuList, List, ListItem, Tooltip } from '@ds';
import { useDispatch, useSelector } from 'react-redux';
import dashboardChartsActions from '@/actions/dashboardChartsActions';
import { BUILT_IN_CHART_TYPES } from '@/lib/appConstants';
import { CodePanelRevision } from '@experiment-management-shared/api/python-panels/types';
import usePythonPanelRevisions from '@/experiment-management-shared/api/python-panels/usePythonPanelRevisions';
import { format } from 'date-fns';
import { ActionMenuListContext } from '@ds/lists/List/ActionMenuList/useActionMenuListContext';
import useIsUserLoggedIn from '@/shared/hooks/useIsUserLoggedIn';
import { isUserAllowedToEditProject } from '@/reducers/projectsReducer';

type PythonPanelOptionsProps = {
  templateId: string;
  revisionId: string;
  onRestartSession: () => void;
};

const PythonPanelOptions = ({
  templateId,
  revisionId,
  onRestartSession
}: PythonPanelOptionsProps) => {
  const dispatch = useDispatch();
  const context = useContext(ActionMenuListContext);
  const isUserLoggedIn = useIsUserLoggedIn();
  const canEdit = useSelector(isUserAllowedToEditProject);

  const { data: revisions } = usePythonPanelRevisions(
    { templateId },
    { refetchOnMount: true, enabled: !!templateId }
  );

  const updatePanelRevisionId = (revision: CodePanelRevision) => {
    dispatch(
      dashboardChartsActions.updateChartForm(BUILT_IN_CHART_TYPES.python, {
        revisionId: revision.id
      })
    );
    context?.closePopover();
  };

  const renderRevisionItem = (revision: CodePanelRevision) => (
    <ListItem
      active={revision.id === revisionId}
      onClick={() => updatePanelRevisionId(revision)}
      key={revision.id}
      dataTest="version-history-item"
    >
      <ListItem.Text
        primary={format(new Date(revision.createdAt), 'MM/dd/yy hh:mm a')}
        secondary={revision.createdBy}
      />
    </ListItem>
  );

  return (
    <ActionMenuList.Body
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ style: { marginTop: 4, marginLeft: 6 } }}
      closeMenuOnSelect
    >
      <Tooltip
        placement="right"
        disableListeners={!!templateId}
        content={
          <div style={{ maxWidth: '178px' }}>
            A version history will be created after saving the panel
          </div>
        }
      >
        <ListItem dataTest="version-history-btn" disabled={!templateId}>
          <ListItem.Text primary="Version history" />
          <ListItem.Submenu offset="-6px 2px 0 0">
            <List>
              <div className={classes.versionHistoryContainer}>
                {revisions?.map(renderRevisionItem)}
              </div>
            </List>
          </ListItem.Submenu>
        </ListItem>
      </Tooltip>

      {isUserLoggedIn && canEdit && (
        <ListItem onClick={onRestartSession} dataTest="restart-session-btn">
          <ListItem.Text primary="Restart session" />
        </ListItem>
      )}
    </ActionMenuList.Body>
  );
};

export default React.memo(PythonPanelOptions);
